import { EventEmitter } from 'events'
import {
    ADD_BOARD,
    BOARDS_LOADED,
    DELETE_BOARD,
    isBoardAction,
    isBoardMoveAction,
    MOVE_BOARD,
    UPDATE_BOARD,
} from './BoardActions'
import Dispatcher, { IAction, IActionPayload } from '../Dispatcher'
import BoardNotFoundError from './errors/BoardNotFoundError'
import { ISwimlane } from '../swimlanes/SwimlaneStore'
import { IBaseMoveError } from '../errors/BaseMoveError'
import { BoardMoveError, BoardMoveErrorTypes } from './errors/BoardMoveError'

/**
 * Represents the raw properties that make up a board.
 * Without considering child components
 */
export interface IBoardBase {
    id: number
    order: number
    name: string
}

/**
 * Represents a kanban board
 */
export interface IBoard extends IBoardBase {
    swimlanes: ISwimlane[]
}

/** Events associated with adding, editing and deleting boards */
export type CRUDEventTypes =
    | EventsEnum.ADD
    | EventsEnum.EDIT
    | EventsEnum.DELETE
    | EventsEnum.MOVE

// Event raised when the state of the baord store changes
export type StateChangeEventType = EventsEnum.STATECHANGE

/**
 * Typeguard, tests if value is a type of IBoard.
 * If true then type will be treated as IBoard for rest of procedure.
 * If false then function is falsey.
 * @param value Object to check type of
 */
function isIBoardBase(value: any | IBoardBase): value is IBoardBase {
    const board = value as IBoardBase
    return !(board.id === undefined || board.order === undefined || !board.name)
}

/** Represents all possible event values for the board store */
enum EventsEnum {
    ADD = 'BOARDSTORE_ADD',
    EDIT = 'BOARDSTORE_EDIT',
    DELETE = 'BOARDSTORE_DELTE',
    MOVE = 'BOARDSTORE_MOVE',
    STATECHANGE = 'BOARDSTORE_STATECHANGE',
}

/**
 * Store for Boards
 */
class BoardStore extends EventEmitter {
    private _state: 'UNINITIALIZED' | 'LOADED' = 'UNINITIALIZED'
    private _boards: { [key: number]: IBoard } = {}
    private _nextId = 0
    private _dispatchToken: string

    /**
     * Exposes the dispatch token for the task store
     */
    get DispatchToken() {
        return this._dispatchToken
    }

    /**
     * Returns the current state of the store
     */
    get state() {
        return this._state
    }

    /**
     * Returns the boards
     */
    get boards() {
        return { ...this._boards }
    }

    /**
     * Computes the initial Total value once the store has loaded
     */
    private getInitialTotal(): number {
        const boards = Object.values(this._boards)
        if (boards.length === 0) return 0
        return (
            boards.reduce((previous, current) =>
                previous.id > current.id ? previous : current
            ).id + 1
        )
    }

    constructor() {
        super()
        this._dispatchToken = Dispatcher.register((action: IAction) => {
            // if action is BOARDS_LOADED then set boards and state
            if (action.actionType === BOARDS_LOADED) {
                this._boards = (action as IActionPayload<{
                    [key: number]: IBoard
                }>).payload
                this._nextId = this.getInitialTotal()
                this._state = 'LOADED'
                this.emitStateChange()
                return
            }
            // typeguard ensures only IBoardAction types get to the switch
            if (isBoardAction(action)) {
                switch (action.actionType) {
                    case ADD_BOARD:
                        this.addBoard(action.payload)
                        break
                    case UPDATE_BOARD:
                        this.editBoard(action.payload)
                        break
                    case DELETE_BOARD:
                        this.removeBoard(action.payload)
                        break
                    default:
                    // do nothing
                }
            } else if (isBoardMoveAction(action)) {
                if (action.actionType === MOVE_BOARD) {
                    const { fromBoardId, toBoardId } = action.payload
                    this.moveBoard(fromBoardId, toBoardId)
                }
            }
        })
    }

    /**
     * Registers an event handler for the BOARDSTORE_STATECHANGE event
     * @param callback Callback to invoke
     */
    addStateChangeListener(callback: () => void) {
        this.on(EventsEnum.STATECHANGE, callback)
    }

    /**
     * Removes a registered event handler for the BOARDSTORE_STATECHANGE event
     * @param callback Callback to remove
     */
    removeStateChangeListener(callback: () => void) {
        this.removeListener(EventsEnum.STATECHANGE, callback)
    }

    /**
     * Subscribes a callback a store event
     * @param eventType Type of event to subscribe to
     * @param callback Callback to register
     */
    addBoardStoreListener(
        eventType: CRUDEventTypes,
        callback: (boardId: number) => void
    ) {
        this.on(eventType, callback)
    }

    /**
     * Unsubscribes a given callback from a board store event
     * @param eventType Type of event to unsubscribe from
     * @param callback Callback to remove
     */
    removeBoardStoreListener(
        eventType: CRUDEventTypes,
        callback: (boardId: number) => void
    ) {
        this.removeListener(eventType, callback)
    }

    /**
     * Emits a state change event
     */
    emitStateChange() {
        this.emit(EventsEnum.STATECHANGE)
    }

    /**
     * Emits a CRUD event
     * @param eventType Type of crud event to emit
     * @param id Board ID being affected
     */
    emitCRUDChange(eventType: CRUDEventTypes, id: number) {
        this.emit(eventType, id)
    }

    /**
     * Adds a new board to the store.
     * Board must have id of -1
     * @param board Board to add
     */
    addBoard(board: IBoardBase) {
        board.id = this._nextId
        board.order = 0
        Object.values(this._boards).forEach((board) => (board.order += 1))
        this._nextId += 1
        this.setBoard({ ...board, swimlanes: [] })
        this.emitCRUDChange(EventsEnum.ADD, board.id)
    }

    /**
     * Updates a board already in the store
     * @param board Board to update
     */
    editBoard(board: IBoardBase) {
        const _board = this.getBoard(board.id)
        const { order, ..._toSave } = board
        this.setBoard({ ..._board, ..._toSave })
        this.emitCRUDChange(EventsEnum.EDIT, board.id)
    }

    /**
     * Removes a board from the BoardStore
     * @param board Board to remove
     */
    removeBoard(board: IBoardBase) {
        const _board = this.getBoard(board.id)
        delete this._boards[board.id]
        const boards = Object.values(this._boards).sort(
            (a, b) => a.order - b.order
        )
        for (let index = _board.order; index < boards.length; index++) {
            boards[index].order -= 1
        }
        this.emitCRUDChange(EventsEnum.DELETE, _board.id)
    }

    /**
     * Moves a board position within the list of boards.
     * @param fromBoardId board swimlane to move belongs to
     * @param toBoardId board to move the swimlane to
     */
    moveBoard(fromBoardId: number, toBoardId: number) {
        const errors: IBaseMoveError<
            BoardMoveErrorTypes,
            BoardNotFoundError
        >[] = []
        let fromBoard: IBoard = {} as IBoard
        try {
            fromBoard = this.getBoard(fromBoardId)
        } catch (error) {
            if (error instanceof BoardNotFoundError)
                errors.push({
                    errorType: BoardMoveErrorTypes.FromBoard,
                    error,
                })
            else throw error
        }
        let insertAt: number = -1
        try {
            insertAt = this.getBoard(toBoardId).order
        } catch (error) {
            if (error instanceof BoardNotFoundError)
                errors.push({
                    errorType: BoardMoveErrorTypes.ToBoard,
                    error,
                })
            else throw error
        }
        // if any errors then throw new task move error
        if (errors.length !== 0) throw new BoardMoveError(errors)
        // beyond this point both to and from will have been validated

        // is a move
        //
        // if from.order is smaller than index at then (moving right):
        // Loop from: from.order + 1 to indexAt + 1, for each iteration subtract 1 from order.
        // e.g. from.order = 1 and indexAt = 6 => loop from 2 to 7, take 1 from order of items
        //
        // if indexAt is smaller than from.order then (moving left):
        // Loop from: indexAt to from.order, for each iteration add 1 to order
        // e.g. indexAt = 1 and from.order = 6 => loop from 1 to 6, add 1 to order of items
        const keys = this.getOrderedBoardsIds()
        const [smallest, largest, amount] =
            fromBoard.order < insertAt
                ? [fromBoard.order + 1, insertAt + 1, -1]
                : [insertAt, fromBoard.order, 1]
        for (let index = smallest; index < largest; index++) {
            const board = this.getBoard(keys[index])
            board.order += amount
            this.setBoard(board)
        }
        // set the order on the board being moved to the desired position
        fromBoard.order = insertAt
        this.setBoard(fromBoard)
        // emit insert
        this.emitCRUDChange(EventsEnum.MOVE, fromBoardId)
    }

    /**
     * Updates a board in the store by overwriting its entry
     * @param board Board to update
     */
    setBoard(board: IBoard) {
        this._boards[board.id] = board
    }

    /**
     * Returns a board object given it's id
     * @param id ID of board to fetch
     */
    getBoard(id: number): IBoard {
        const result = this._boards[id]
        if (!result) throw new BoardNotFoundError(id)
        return { ...result }
    }

    /**
     * Gets the id of every board in the system
     * ordered by their order property ascending
     * @returns array of every board ID in the board store
     */
    getOrderedBoardsIds(): number[] {
        return Object.values(this._boards)
            .sort((a, b) => a.order - b.order)
            .map((x) => x.id)
    }
}

export default new BoardStore()
export { isIBoardBase, EventsEnum }
